import { useState, useEffect } from 'react';

import { Security } from '@okta/okta-react';
import axios from 'axios';
import localforage from 'localforage';
import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';
import { StripeProvider } from 'react-stripe-elements';
import { PersistGate } from 'redux-persist/integration/react';

import { configWithDefaults } from '@@/constants/wlConfig';
import { ConfigProvider } from '@@/services/useConfig';
import { store, persistor } from '@@/store';
import { getCmsContent, getFeatureFlags } from '@@/store/actions';
import TenantTheme from '@@/themes/TenantTheme';

import analytics from '../../analytics';
import api from '../../api';
import SpinnerView from '../SpinnerView';

import Router from './Router';

/*
 * Axios config
 */

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(error);
    return Promise.reject(error);
  }
);

const SureAuth = () => {
  const { oktaAccessToken, oktaIdToken, user } = store.getState();
  // Set global Auth header
  if (oktaAccessToken) {
    api.setAuthHeader(oktaAccessToken.accessToken);
  }

  if (oktaIdToken) {
    analytics.identifyUser(oktaIdToken.claims, user || {});
  }
  return null;
};

export default function App() {
  const [isLoadingFeatureFlags, setIsLoadingFeatureFlags] = useState(false);
  const [stripe, setStripe] = useState<ReturnType<typeof window.Stripe> | null>(
    null
  );

  const { localforageDbName, cssFiles } = configWithDefaults();

  /**
   * Configure localforage configs
   */
  useEffect(
    function configureLocalforage() {
      localforage.config({
        name: localforageDbName,
        storeName: localforageDbName,
      });
    },
    [localforageDbName]
  );

  useEffect(function loadCmsContent() {
    store.dispatch(getCmsContent(['footer', 'copyright']));
  }, []);

  useEffect(function loadFeatureFlags() {
    async function fetchFlags() {
      setIsLoadingFeatureFlags(true);
      try {
        await store.dispatch(getFeatureFlags());
      } catch {
        /* fallback to feature flag defaults */
      }
      setIsLoadingFeatureFlags(false);
    }

    fetchFlags();
  }, []);

  useEffect(
    function setUpStripe() {
      const stripeScript = document.querySelector('#stripe-js');
      if (window.Stripe) {
        setStripe(
          window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')
        );
      } else if (stripeScript) {
        stripeScript.addEventListener('load', () => {
          setStripe(
            window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')
          );
        });
      }
    },
    [setStripe]
  );

  if (isLoadingFeatureFlags) {
    return <SpinnerView data-testid="App_spinner" />;
  }

  return (
    <TenantTheme cssFiles={cssFiles}>
      <Helmet>
        <script
          src="https://cdnjs.cloudflare.com/ajax/libs/bodymovin/4.13.0/bodymovin_light.min.js"
          async
        />
      </Helmet>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ConfigProvider>
            <StripeProvider stripe={stripe}>
              <Security
                issuer={process.env.REACT_APP_OKTA_ISSUER}
                clientId={process.env.REACT_APP_OKTA_CLIENT_ID}
                redirectUri={`${window.location.origin}${process.env.REACT_APP_OKTA_REDIRECT_URI}`}
                responseType={['id_token', 'token']}
                onSessionExpired={() => {}}
                pkce={false}
                cookies={{
                  secure: process.env.REACT_APP_ENV !== 'development',
                }}
              >
                <SureAuth />
                <Router />
              </Security>
            </StripeProvider>
          </ConfigProvider>
        </PersistGate>
      </Provider>
    </TenantTheme>
  );
}
